import React from 'react';
import axios from 'axios';
import numeral from 'numeral';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Grid, Typography } from '@material-ui/core';
import LeadConfirmation from './LeadConfirmation';
import { Button } from '../../formikFormInputs';
import { stagingServerUrl } from '../../../server/constants';
import { SnackbarContext } from '../../Snackbar/Context';
import { SnackBarSeverity } from '../../../utils/enums';
import { defaultPhoneNumber, errorLog } from '../../../utils/commonUtils';
import { initFalse } from '../../../utils/constants';


enum ContactTimeEnum {
  Morning = 1,
  AfterNoon,
  Evening
}

const WeWillBeInTouch: React.FC = () => {
  const router = useRouter();
  const snackbar = React.useContext(SnackbarContext);

  const id = router.query.id;
  const email = decodeURIComponent((router.query.email || '') as string);
  const [appointmentConfirmation, setAppointmentConfirmation] = React.useState<boolean>(initFalse);
  const [deleteConfirmation, setDeleteConfirmation] = React.useState<boolean>(initFalse);
  const [openConfirmation, setOpenConfirmation] = React.useState<boolean>(initFalse);
  const [selected, setSelected] = React.useState<number>(0);

  const handleRemoveLead = () => {
    const [delAppointmentUrl, payload] = [
      `${stagingServerUrl}/leads/deleteappointment`,
      {email, id: numeral(id).value()}
    ];

    axios.post(delAppointmentUrl, payload)
      .then(() => {
        setDeleteConfirmation(!initFalse);
        setOpenConfirmation(initFalse);
      })
      .catch((error: any) => {
        const msg: any = error && error.response && error.response.data && error.response.data.message;
        errorLog(error, error && error.response && error.response.data && error.response.data.message);
        snackbar.showMessage(msg, SnackBarSeverity.Error);
      });
  };
  const setAppointment = async () => {
    if (!selected) {
      snackbar.showMessage('Select contact time preference.', SnackBarSeverity.Error);
    }

    const _selectedOption = fieldObjDateTime.find(a => a.value === selected);
    const [leadsAppointmentUrl, payload] = [
      `${stagingServerUrl}/leads/setappointment`,
      {
        email,
        id: numeral(id).value(),
        specificDate: null,
        specificTime: _selectedOption?.caption
      }
    ];

    await axios.put(leadsAppointmentUrl, payload)
      .then(() => {
        setAppointmentConfirmation(!initFalse);
      })
      .catch((error: any) => {
        const msg: any = error && error.response && error.response.data && error.response.data.message;
        errorLog(error && error.response && error.response.data && error.response.data.message);
        snackbar.showMessage(msg, SnackBarSeverity.Error);
      });
  };
  const fieldObjDateTime = [
    {
      caption: 'Morning (9:00 AM - Afternoon)',
      selected: ContactTimeEnum.Morning === selected,
      value: ContactTimeEnum.Morning,
    },
    {
      caption: 'Afternoon (Noon - 5:00 PM)',
      selected: ContactTimeEnum.AfterNoon === selected,
      value: ContactTimeEnum.AfterNoon
    },
    {
      caption: 'Evening (5:00 PM - 8:00 PM)',
      selected: ContactTimeEnum.Evening === selected,
      value: ContactTimeEnum.Evening
    }
  ];

  React.useEffect(() => {
    if (selected) {
      setAppointment().then();
    }
  }, [selected]);

  return (
    <>
      {
        openConfirmation &&
        <LeadConfirmation close={() => setOpenConfirmation(initFalse)} success={handleRemoveLead} />
      }

      <Grid container spacing={2}>
        {
          !appointmentConfirmation && !deleteConfirmation &&
          <>
            <Grid item xs={12}>

              <div className="second-step">

                <Typography variant="h2" color="primary" className="positionR">
                  <div className="arrow arrowAppoint" /> Next Steps: We’ll Be in Touch!
                </Typography>
                <Typography variant="subtitle1" color="primary" className="mb20">
                  There is a lot more to Kai-Zen<sup>&reg;</sup> than we can show here so one of our<br/> specialists will reach out to answer any questions you may have.
                </Typography>
              </div>
              <Typography variant="subtitle2" component="strong" color="primary" className="mb20 floatLeft w100">
               Do you have a contact time preference?
              </Typography>
            </Grid>
            <Grid container spacing={3} justifyContent="center">
              {
                fieldObjDateTime.map((field, index) => {
                  return (
                    <div className="mr15 mr0Small" key={index}>
                      <Button
                        {...field}
                        color="primary"
                        className={field.selected ? 'active mb15' : 'mb15'}
                        label={field.caption}
                        onClick={() => setSelected(field.value)}
                        size="large"
                        value={field.value}
                        variant="outlined"
                      />
                    </div>
                  )
                })
              }
              <Grid item xs={12}>
                <Typography
                  color="primary"
                  className="w100 itemHover"
                  onClick={() => setOpenConfirmation(true)}
                  style={{cursor: 'pointer'}}
                  variant={'body1'}
                >
                  I’m no longer interested
                </Typography>
              </Grid>
            </Grid>
          </>
        }
        {
          appointmentConfirmation &&
          <Grid item xs={12}>
            <Typography variant="h2" color="primary">
              Thank You!
            </Typography>
            <Typography variant={'body1'} color="primary" className="textLight w100 mt40">
              You are scheduled to be contacted in
              the <strong>{fieldObjDateTime.find(a => a.value === selected)?.caption}</strong> by
              an NIW specialist.<br/> We look forward to answering any questions you may have.
            </Typography>
          </Grid>
        }
        {
          deleteConfirmation &&
          <Grid item xs={12}>
            <Typography variant="h2">
              You've Opted Out
            </Typography>
            <Typography variant={'body1'} className="textLight w100 mt40">
              We won't contact you with information regarding this strategy. If
              this was a mistake please contact us at
              <Link href={`tel:${defaultPhoneNumber}`}>{defaultPhoneNumber}</Link>
              ext-201 for assistance.
            </Typography>
          </Grid>
        }
      </Grid>
    </>
  );
};

export default WeWillBeInTouch;
